'use client';

import React, { useRef, useState, useMemo } from 'react';
import styled from 'styled-components';
import Image from 'next/image';
import { Chart } from './Chart';
import { ChartHamburgerMenu } from './combined/ChartHamburgerMenu';
import { ChartSkeleton } from './ChartSkeleton';
import { Skeleton } from '@/components/skeletons';
import { Breadcrumbs } from '@/components/ui-components/Breadcrumbs/Breadcrumbs';
import { theme } from '../../config/theme';
import type { ChartLayout, ChartConfig, ChartData } from '../../types/chart';

// Domain color palette - consistent colors for each domain across all metrics
const DOMAIN_COLORS = [
  theme.colors.gray[800], // Dark brown/black
  theme.colors.status.errorAlt, // Red
  theme.colors.primary.accent, // Orange
  theme.colors.gray[400], // Black
  'rgba(211, 51, 36, 0.4)', // Light beige/tan
  theme.colors.status.success, // emerald (for additional domains)
  theme.colors.status.info, // Blue (for additional domains)
  theme.colors.chart.purple, // Purple (for additional domains)
  theme.colors.chart.cyan, // cyan/light blue
  theme.colors.chart.lime,
  theme.colors.status.warning,
  theme.colors.chart.pink, // Pink
  theme.colors.chart.brown, // Brown
];

// ============================================================================
// Styled Components
// ============================================================================

const ChartContainer = styled.div`
  background: ${theme.colors.white};
  border-radius: 8px;
  border: 1px solid ${theme.colors.border.primary};
  transition: all 0.3s ease;
  width: 100%;
  overflow: visible;
`;



const RightSectionWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;


const ChartContent = styled.div`
  overflow: visible;
  min-height: 400px;
  position: relative;
  border-radius: 8px;
`;

const TooltipContainer = styled.div`
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: 8px;
  padding: 16px;
  box-shadow: ${theme.shadows.md};
  display: flex;
  flex-direction: column;
  gap :16px;
  max-width: 350px;
`;

const TooltipTitle = styled.div`
  font-size: 14px;
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colors.gray[900]};
  font-family: 'Open Sans', sans-serif;
  word-break: break-word;
`;

const TooltipRow = styled.div`
   font-size: 14px;
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colors.gray[900]};
  display: flex;
  align-items: center;

  font-family: 'Open Sans', sans-serif;

`;

const TooltipLabel = styled.span`
  font-weight: ${theme.fontWeights.medium};
`;

const TooltipValue = styled.span<{ $color: string }>`
  font-weight: ${theme.fontWeights.bold};
  color: ${({ $color }) => $color};
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  color: ${theme.colors.gray[500]};
  font-size: 14px;
`;

const TooltipImageWrapper = styled.div`
  position: relative;
  height: 170px;
  width: 100%;
  background: ${theme.colors.gray[50]};
  border-radius: 4px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #D9D9DC;
`;

const TooltipImage = styled(Image)`
  object-fit: contain;
`;

const ImageSkeleton = styled(Skeleton)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

const LoadingText = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: ${theme.colors.gray[500]};
  font-size: 14px;
  font-weight: ${theme.fontWeights.medium};
  font-family: 'Open Sans', sans-serif;
  z-index: 1;
`;

// Tooltip Image Component with loading state
const TooltipImageWithLoading: React.FC<{ src: string; alt: string }> = ({ src, alt }) => {
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);

  // Reset loading state when src changes
  React.useEffect(() => {
    setImageLoading(true);
    setImageError(false);
  }, [src]);

  // Check if the image is from an external domain that might not be configured
  // Use unoptimized for external images to avoid hostname configuration issues
  const isExternalImage = React.useMemo(() => {
    if (!src) return false;
    try {
      const url = new URL(src);
      // Check if it's from glassx.ai domain (which should be configured)
      const isGlassxDomain = url.hostname.includes('glassx.ai');
      // Use unoptimized for external images that aren't from configured domains
      // This allows images to load even if hostname isn't in next.config
      return !isGlassxDomain;
    } catch {
      // If URL parsing fails, assume it's a relative path (optimized)
      return false;
    }
  }, [src]);

  return (
    <TooltipImageWrapper>
      {imageLoading && !imageError && (
        <>
          <ImageSkeleton width="100%" height="100%" animation="pulse" />
          <LoadingText>Loading...</LoadingText>
        </>
      )}
      <TooltipImage
        src={src}
        alt={alt}
        width={350}
        height={170}
        onLoad={() => setImageLoading(false)}
        onError={() => {
          setImageLoading(false);
          setImageError(true);
        }}
        style={{ display: imageError ? 'none' : 'block' }}
        unoptimized={isExternalImage}
      />
      {imageError && (
        <div style={{ padding: '20px', textAlign: 'center', color: theme.colors.gray[500], fontSize: '14px', fontWeight: theme.fontWeights.medium, fontFamily: 'Open Sans, sans-serif' }}>
          Image failed to load
        </div>
      )}
    </TooltipImageWrapper>
  );
};

// ============================================================================
// Types
// ============================================================================

export interface VitalsData {
  pageLabel: string;
  ttfb: number;
  fcp: number;
  lcp: number;
  tbt: number;
  cls: number;
  ttfb_screenshot: string;
  fcp_screenshot: string;
  lcp_screenshot: string;
  cls_screenshot: string;
  tbt_screenshot: string;
}

export interface VitalsByDomainBarChartProps {
  title?: string;
  subtitle?: string;
  data: VitalsData[];
  height?: number;
  loading?: boolean;
  className?: string;
  tickLabelColor?: string;
  xAxisTickLabelSpacing?: number;
  xAxisLabel?: string;
  yAxisLabel?: string;
  tickMargin?: number;
}

// ============================================================================
// Helper Functions
// ============================================================================

const getStatus = (value: number, metric: string): 'good' | 'caution' | 'critical' => {
  switch (metric) {
    case 'ttfb':
      if (value <= 0.8) return 'good';
      if (value <= 1.8) return 'caution';
      return 'critical';
    case 'fcp':
      if (value <= 1.8) return 'good';
      if (value <= 3.0) return 'caution';
      return 'critical';
    case 'lcp':
      if (value <= 2.5) return 'good';
      if (value <= 4.0) return 'caution';
      return 'critical';
    default:
      return 'good';
  }
};

const getStatusColor = (status: 'good' | 'caution' | 'critical'): string => {
  switch (status) {
    case 'good':
      return '#10b981'; // green
    case 'caution':
      return '#f59e0b'; // yellow/orange
    case 'critical':
      return '#ef4444'; // red
  }
};

const formatValue = (value: number, useSeconds: boolean): string => {
  if (useSeconds) {
    return `${value.toFixed(2)}s`;
  }
  return `${value.toFixed(0)}ms`;
};

// Metric name mapping: abbreviation to full name
const METRIC_NAMES: Record<string, string> = {
  'TTFB': 'Time to First Byte',
  'FCP': 'First Contentful Paint',
  'LCP': 'Largest Contentful Paint',
};

// Custom tooltip component for VitalsByDomainBarChart
// This will be created inside the component to access useSeconds

// ============================================================================
// Component
// ============================================================================

export const VitalsByDomainBarChart: React.FC<VitalsByDomainBarChartProps> = ({
  title = 'Vitals by Domain',
  subtitle = 'View Web Vitals across your domains to pinpoint which are slow and what\'s causing the performance issues',
  data,
  height = 400,
  loading = false,
  className,
  tickLabelColor = '#000000',
  xAxisLabel = 'Vitals',
  yAxisLabel = 'Time',
  tickMargin = 10,
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const [zoomEnabled, setZoomEnabled] = useState(false);

  // Calculate conversion factor and axis config first
  // Values are in milliseconds - if max >= 1000, convert to seconds for display
  const { useSeconds, conversionFactor, yAxisConfig } = useMemo(() => {
    const maxValue = Math.max(
      ...data.flatMap(item => [item.ttfb || 0, item.fcp || 0, item.lcp || 0])
    );
    
    // Check if we need to convert to seconds (values >= 1000ms)
    const useSeconds = maxValue >= 1000;
    const conversionFactor = useSeconds ? 1000 : 1;
    const maxValueInDisplayUnit = maxValue / conversionFactor;
    
    let maxTick: number;
    let tickInterval: number;
    const ticks: number[] = [];
    
    if (useSeconds) {
      // For seconds: determine interval based on max value
      if (maxValueInDisplayUnit <= 2) {
        // Small values: use 0.5s intervals
        tickInterval = 0.5;
        maxTick = Math.ceil(maxValueInDisplayUnit / tickInterval) * tickInterval;
      } else if (maxValueInDisplayUnit <= 10) {
        // Medium values: use 1s intervals
        tickInterval = 1;
        maxTick = Math.ceil(maxValueInDisplayUnit / tickInterval) * tickInterval;
      } else {
        // Large values: use 2s intervals
        tickInterval = 2;
        maxTick = Math.ceil(maxValueInDisplayUnit / tickInterval) * tickInterval;
      }
      
      // Generate ticks
      for (let i = 0; i <= maxTick; i += tickInterval) {
        ticks.push(i);
      }
    } else {
      // For milliseconds: use 200ms intervals
      tickInterval = 200;
      maxTick = Math.ceil(maxValueInDisplayUnit / tickInterval) * tickInterval;
      
      // Generate ticks
      for (let i = 0; i <= maxTick; i += tickInterval) {
        ticks.push(i);
      }
    }
    
    return {
      useSeconds,
      conversionFactor,
      yAxisConfig: { maxTick, ticks, useSeconds },
    };
  }, [data]);

  // Transform data for grouped bar chart using ChartData format
  // Each domain becomes a series (trace) with all metrics as data points
  // The convertToRechartsData function will combine these into Recharts format
  // Convert milliseconds to seconds if values are >= 1000ms
  const chartData = useMemo(() => {
    const metrics = ['TTFB', 'FCP', 'LCP'];
    
    // Create one ChartData entry per domain (series)
    // All domains share the same x values (metrics)
    return data.map((item, domainIndex) => {
      const domainColor = DOMAIN_COLORS[domainIndex % DOMAIN_COLORS?.length];
      
      // Create y values for all metrics for this domain
      // Convert to seconds if needed
      const yValues = metrics.map(metric => {
        const metricKey = metric.toLowerCase() as 'ttfb' | 'fcp' | 'lcp';
        const value = item[metricKey];
        return value != null ? value / conversionFactor : 0;
      });
      
      return {
        type: 'bar' as const,
        name: `domain${domainIndex}`, // This will be used as the series name/key
        x: metrics, // All series share the same x values
        y: yValues, // Y values for this domain across all metrics (in seconds if >= 1000ms)
        marker: {
          color: domainColor,
        },
      } as ChartData;
    });
  }, [data, conversionFactor]);

  // Create table data for export
  const tableData = useMemo(() => {
    return data.map(item => ({
      domain: item.pageLabel,
      ttfb: item.ttfb != null ? `${(item.ttfb / 1000).toFixed(2)}s` : 'N/A',
      fcp: item.fcp != null ? `${(item.fcp / 1000).toFixed(2)}s` : 'N/A',
      lcp: item.lcp != null ? `${(item.lcp / 1000).toFixed(2)}s` : 'N/A',
    }));
  }, [data]);

  // Handle bar click to open screenshot
  const handleBarClick = React.useCallback((event: { 
    activeLabel?: string | number; 
    activePayload?: Array<{ 
      name: string; 
      value: number | string;
    }> 
  }) => {
    if (!event.activePayload || event.activePayload?.length === 0) return;
    
    const item = event.activePayload[0];
    // The dataKey is in the name field for bar charts (e.g., "domain0", "domain1", etc.)
    const domainKey = item.name;
    
    // Extract domain index from the key
    const domainIndexMatch = domainKey.match(/domain(\d+)/);
    const domainIndex = domainIndexMatch ? parseInt(domainIndexMatch[1], 10) : -1;
    
    if (domainIndex < 0 || domainIndex >= data?.length) return;
    
    // Get metric abbreviation from activeLabel
    // Convert to string if it's a number, and validate it exists
    const metricAbbr = event.activeLabel != null 
      ? String(event.activeLabel) 
      : null;
    if (!metricAbbr || typeof metricAbbr !== 'string') return;
    
    // Get the screenshot URL for this metric
    const metricKey = metricAbbr.toLowerCase() as 'ttfb' | 'fcp' | 'lcp';
    const screenshotKey = `${metricKey}_screenshot` as keyof VitalsData;
    const screenshotUrl = data[domainIndex][screenshotKey] as string | undefined;
    
    if (screenshotUrl && screenshotUrl !== process.env.NEXT_PUBLIC_SCRAPPER_API_BASE_URL) {
      window.open(screenshotUrl, '_blank', 'noopener,noreferrer');
    }
  }, [data]);

  // Custom tooltip component for VitalsByDomainBarChart
  // Created here to access useSeconds and data from the component scope
  const VitalsTooltip = React.useCallback(({ active, payload }: {
    active?: boolean;
    payload?: Array<{
      name: string;
      value: number;
      dataKey: string;
      payload: Record<string, string | number>;
      color?: string;
    }>;
  }) => {
    if (active && payload && payload?.length > 0) {
      const item = payload[0];
      const domainKey = item.dataKey; // This will be like "domain0", "domain1", etc.
      
      // Extract domain index from the key (e.g., "domain0" -> 0)
      const domainIndexMatch = domainKey.match(/domain(\d+)/);
      const domainIndex = domainIndexMatch ? parseInt(domainIndexMatch[1], 10) : -1;
      
      // Get domain label from original data
      const domainLabel = domainIndex >= 0 && domainIndex < data?.length 
        ? data[domainIndex].pageLabel 
        : '';
      
      // Get metric abbreviation from payload
      const metricAbbr = item.payload.x as string;
      // Get full metric name
      const metricFullName = METRIC_NAMES[metricAbbr] || metricAbbr;
      
      // Calculate status from the original value (before conversion to seconds)
      // We need to get the original metric value in milliseconds to calculate status
      const metricKey = metricAbbr.toLowerCase() as 'ttfb' | 'fcp' | 'lcp';
      const originalValueMs = domainIndex >= 0 && domainIndex < data?.length
        ? data[domainIndex][metricKey]
        : null;
      
      // Calculate status using original value in milliseconds
      const status = originalValueMs != null 
        ? getStatus(originalValueMs / 1000, metricKey) // getStatus expects seconds
        : 'good';
      const statusColor = getStatusColor(status);

      // Get screenshot URL
      const screenshotKey = `${metricKey}_screenshot` as keyof VitalsData;
      const screenshotUrl = (domainIndex >= 0 && domainIndex < data?.length)
        ? (data[domainIndex][screenshotKey] as string | undefined)
        : undefined;

      return (
        <TooltipContainer>
          {domainLabel && <TooltipTitle>{domainLabel}</TooltipTitle>}
          <TooltipRow>
            <TooltipLabel>{metricFullName} : &nbsp;</TooltipLabel>
            <TooltipValue $color={statusColor}>
              {formatValue(item.value, useSeconds)}
            </TooltipValue>
          </TooltipRow>
          {screenshotUrl && (
            <TooltipImageWithLoading src={screenshotUrl} alt={metricFullName} />
          )}
        </TooltipContainer>
      );
    }
    return null;
  }, [useSeconds, data]);

  // Chart theme with domain colors
  const chartTheme = useMemo(() => ({
    accentColors: DOMAIN_COLORS,
    gridColor: '#D9D9DC',
    textColor: theme.colors.gray[600],
  }), []);

  // Chart layout configuration
  const chartLayout: ChartLayout = useMemo(() => ({
    height,
    margin: {
      top: 20,
      right: 30,
      left: 20,
      bottom: 24, // Add spacing above x-axis tick labels
    },
    xaxis: {
      type: 'category' as const,
      showgrid: false,
      showline: false,
      // Show x-axis category labels (TTFB, FCP, LCP)
      hideTicks: false,
      domain: [0, 'dataMax'],
      tickfont: {
        color: tickLabelColor,
      },
      tickMargin: tickMargin,
      title: {
        text: xAxisLabel,
        font: {
          size: 12,
          color: '#6E6E6E',
          family: 'Open Sans, sans-serif',
        },
      },
    },
    yaxis: {
      type: 'linear' as const,
      showgrid: true,
      gridcolor: theme.colors.gray[200],
      showline: false,
      // Show y-axis tick labels (convert ms to s if >= 1000)
      hideTicks: false,
      domain: [0, yAxisConfig.maxTick],
      ticks: yAxisConfig.ticks,
      tickFormatter: (value: number) => {
        // Format based on whether we're using seconds or milliseconds
        if (yAxisConfig.useSeconds) {
          return `${value}s`;
        } else {
          return `${value}ms`;
        }
      },
      tickfont: {
        color: tickLabelColor,
      },
      tickMargin: tickMargin,
      title: {
        text: yAxisLabel,
        font: {
          size: 12,
          color: '#6E6E6E',
          family: 'Open Sans, sans-serif',
        },
      },
    },
    showlegend: false,
  }), [height, yAxisConfig, tickLabelColor, xAxisLabel, yAxisLabel, tickMargin]);

  // Chart config
  const chartConfig: ChartConfig = useMemo(() => ({
    scrollZoom: false,
    responsive: true,
  }), []);


  if (loading) {
    return <ChartSkeleton type="bar" height={`${height}px`} showLegend />;
  }

  if (data?.length === 0) {
    return (
      <ChartContainer className={className}>
       
          <Breadcrumbs 
            items={[{ label: title }]} 
            style={{ borderRadius: '8px'}}
          />
        <EmptyState>No data available</EmptyState>
      </ChartContainer>
    );
  }

  return (
    <ChartContainer ref={chartRef} className={className}>
    
        <Breadcrumbs 
          items={[{ label: title }]} 
          style={{ borderRadius: '8px'}}
          rightSection={
            <RightSectionWrapper>
              <ChartHamburgerMenu
                chartTitle={title}
                tableData={tableData}
                chartContainerRef={chartRef}
                onZoomToggle={setZoomEnabled}
                initialZoomEnabled={zoomEnabled}
              />
            </RightSectionWrapper>
          }
        />
      <ChartContent>
        <Chart
          data={chartData}
          layout={chartLayout}
          config={chartConfig}
          chartTheme={chartTheme}
          customTooltip={VitalsTooltip}
          onClick={handleBarClick}
        />
      </ChartContent>
    </ChartContainer>
  );
};

